import React from 'react';
import CreatableSelect from 'react-select/creatable';

const EMPTY_INPUT_MESSAGE = 'Type something to add an option';
const INVALID_INPUT_MESSAGE = 'Enter numbers only';
const DUPLICATE_VALUE_MESSAGE = 'This value is already selected';

const parseNumericInput = inputValue => {
	const trimmed = (inputValue || '').trim();

	if (!trimmed) {
		return { type: 'empty' };
	}

	if (!/^\d+$/.test(trimmed)) {
		return { type: 'invalid' };
	}

	const num = parseInt(trimmed, 10);
	if (Number.isNaN(num)) {
		return { type: 'invalid' };
	}

	return { type: 'valid', num };
};

const isDuplicateValue = (num, selectValue, selectOptions) => {
	const existingOptions = [...(selectValue || []), ...(selectOptions || [])];
	return existingOptions.some(option => option.value === num);
};

const defaultIsValidNewOption = (inputValue, selectValue, selectOptions) => {
	const parsed = parseNumericInput(inputValue);

	if (parsed.type !== 'valid') {
		return false;
	}

	return !isDuplicateValue(parsed.num, selectValue, selectOptions);
};

const defaultGetNewOptionData = (inputValue, optionLabel) => {
	const num = parseInt(inputValue.trim(), 10);
	return {
		label: optionLabel,
		value: num,
		__isNew__: true
	};
};

export default function CreatableMultiSelect({
	value,
	onChange,
	placeholder = ' ',
	isClearable = true,
	isValidNewOption = defaultIsValidNewOption,
	getNewOptionData = defaultGetNewOptionData,
	className = 'ats-multi-dropdown ats-multi-dropdown-grouped react-select',
	options = [],
	emptyInputMessage = EMPTY_INPUT_MESSAGE,
	invalidInputMessage = INVALID_INPUT_MESSAGE,
	duplicateValueMessage = DUPLICATE_VALUE_MESSAGE,
	menuPlacement = 'auto',
	menuPosition = 'fixed',
	closeMenuOnScroll,
	menuPortalTarget,
	styles
}) {
	const noOptionsMessage = ({ inputValue }) => {
		const parsed = parseNumericInput(inputValue);

		if (parsed.type === 'empty') {
			return emptyInputMessage;
		}

		if (parsed.type === 'invalid') {
			return invalidInputMessage;
		}

		if (isDuplicateValue(parsed.num, value, options)) {
			return duplicateValueMessage;
		}

		return emptyInputMessage;
	};

	return (
		<CreatableSelect
			isMulti
			isClearable={isClearable}
			value={value}
			onChange={onChange}
			placeholder={placeholder}
			options={options}
			className={className}
			classNamePrefix="react-select"
			menuPlacement={menuPlacement}
			menuPosition={menuPosition}
			closeMenuOnScroll={closeMenuOnScroll}
			menuPortalTarget={menuPortalTarget}
			styles={styles}
			createOptionPosition="first"
			openMenuOnFocus
			isValidNewOption={isValidNewOption}
			getNewOptionData={getNewOptionData}
			formatCreateLabel={inputValue => `Add "${inputValue.trim()}"`}
			noOptionsMessage={noOptionsMessage}
			components={{
				IndicatorSeparator: null,
				DropdownIndicator: null
			}}
		/>
	);
}
